import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './expandableDescription.css';

const COLLAPSED_LINES = 4;

/**
 * Long text that is clamped to a few lines with a fade-out at the bottom and a
 * "Show more" toggle that smoothly reveals the rest.
 */
const ExpandableDescription = ({ text, className = '', collapsedLines = COLLAPSED_LINES }) => {
  const contentRef = useRef(null);
  const lastWidthRef = useRef(0);
  const [expanded, setExpanded] = useState(false);
  const [heights, setHeights] = useState({ collapsed: null, full: null });

  const measure = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;

    const styles = window.getComputedStyle(el);
    const parsedLineHeight = parseFloat(styles.lineHeight);
    const lineHeight = Number.isNaN(parsedLineHeight)
      ? parseFloat(styles.fontSize) * 1.3
      : parsedLineHeight;

    setHeights({ collapsed: lineHeight * collapsedLines, full: el.scrollHeight });
  }, [collapsedLines]);

  // Measure before paint so the text never flashes at full height.
  useLayoutEffect(() => {
    lastWidthRef.current = contentRef.current ? contentRef.current.clientWidth : 0;
    measure();
  }, [measure, text]);

  // Re-measure when the available width changes (line count changes with it).
  useEffect(() => {
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      if (el.clientWidth === lastWidthRef.current) return;
      lastWidthRef.current = el.clientWidth;
      measure();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure]);

  // Leading/trailing blank lines survive `white-space: pre-line` and would both
  // pad the clamp and make the content look overflowing when it isn't.
  const content = typeof text === 'string' ? text.trim() : text;

  const { collapsed, full } = heights;
  const isOverflowing = collapsed !== null && full > collapsed + 1;
  const isClamped = isOverflowing && !expanded;

  return (
    <div className={`expandable-description ${className}`.trim()}>
      <div
        ref={contentRef}
        className={`expandable-description-content${isClamped ? ' is-clamped' : ''}`}
        style={collapsed === null ? undefined : { maxHeight: expanded ? full : collapsed }}
      >
        {content}
      </div>
      {isOverflowing && (
        <button
          type="button"
          className={`expandable-description-toggle${expanded ? ' is-expanded' : ''}`}
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
        >
          {expanded ? 'Show less' : 'Show more'}
          <i className="bi bi-chevron-down"></i>
        </button>
      )}
    </div>
  );
};

export default ExpandableDescription;
